import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { convertScoringOverrideProCategoryToMain, getAllowedDrawEliminationTypes, getAllowedScoringFormats, getDefaultAllottedTimeForScoringType, getDefaultEliminationType, getDrawEliminationTypeId, getEliminationTypeIdFromDrawData, getFormValuesFromEventDrawScoringTypeOverrides, getParentDrawEliminationTypeIdFromDrawData, getProDrawEliminationTypes } from '../../../utils/events';
import { getDrawDataForEdit } from '../../../api/tmsEventsApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { ballTypeIds, divisionFormatTypeIds, drawAutofillTypeIds, drawCategoryTypeIds, drawEliminationTypeIds, ficFormatTypeIds, ficFormatTypeIdsDisabledFIC16, ficFormats, officialsTypeIds, playOffDrawDetails, playOffDrawDetailsForAutomatedDraws, scoringTypeOverrides, scoringTypeOverridesForAutomatedDraws, sportTypeIds } from '../../../utils/eventConstants';
import { drawSizes, drawSizesCompass, drawSizesMP, ficOrFibDrawSizes } from '../../../utils/constants';
import { useStatsig } from '../../hooks/useStatsig';

const useDrawEdit = (params) => {
    const {
        sportTypeId,
        eventAllottedMatchTimes,
        isInEditMode,
        eventId,
        draw,
        timeZone,
        eventTypeId,
        forceDoubles = false,
        allowMomentTimeZoneOverride,
    } = params;

    const coreUI = useSelector(({ coreUI }) => coreUI);

    const {
        drawEliminationTypes,
        scoringTypes,
    } = coreUI.types.tms.division.tournament;

    const { checkGate, featureGates } = useStatsig();
    const isFeedInConsolationFlagEnabled = checkGate(featureGates.FEED_IN_CONSOLATION_DRAWS);
    const isProDrawCreationEnabled = checkGate(featureGates.PRO_DRAWS_CREATION);
    const isAllottedTimeFlagEnabled = checkGate(featureGates.ALLOTTED_MATCH_TIMES);
    const selectableDrawEliminationTypes = getAllowedDrawEliminationTypes(
        drawEliminationTypes,
        isFeedInConsolationFlagEnabled,
        isProDrawCreationEnabled,
        true,
        false,
    );
    const proDrawEliminationTypes = getProDrawEliminationTypes(
        drawEliminationTypes,
        isFeedInConsolationFlagEnabled,
        isProDrawCreationEnabled,
        true,
    );

    const selectableScoringTypes = scoringTypes.filter(type => {
        return type.sportTypeId === sportTypeId;
    });
    const [drawDataFromEditSpecificApi, setDrawDataFromEditSpecificApi] = useState(null);
    const [playOffDrawOptions, setPlayOffDrawOptions] = useState([]);
    const [scoringFormatOverrideOptions, setScoringFormatOverrideOptions] = useState([]);

    const defaultEliminationTypeId = getDefaultEliminationType(eventTypeId, sportTypeId);
    const [allowRoundResize, setAllowRoundResize] = useState(defaultEliminationTypeId === drawEliminationTypeIds.AD_HOC);
    const [selectableDrawSizes, setSelectableDrawSizes] = useState(forceDoubles ? drawSizes('teams') : drawSizes('players'));
    const [showFicFormatTypeField, setShowFicFormatTypeField] = useState(defaultEliminationTypeId === drawEliminationTypeIds.FEED_IN_CONSOLATION);
    const [ficFormatTypes, setFicFormatTypes] = useState(ficFormatTypeIds);
    const [dateToDisplay, setDateToDisplay] = useState(null);
    const [isDrawSizeChanged, setIsDrawSizeChanged] = useState(false);
    const [isDoubleEliminationDraw, setIsDoubleEliminationDraw] = useState(defaultEliminationTypeId === drawEliminationTypeIds.DOUBLE_ELIMINATION);

    const getScoringFormats = (values) => {
        if (sportTypeId === sportTypeIds.PICKLEBALL) {
            return selectableScoringTypes;
        }

        return getAllowedScoringFormats(selectableScoringTypes, eventTypeId, values.formatTypeId, values.ballTypeId);
    };

    const onChangeScoringType = (formApi) => (scoringTypeId) => {
        const isAllottedMatchTimesDisabled = drawDataFromEditSpecificApi?.hasScheduledMatches;
        if (!isAllottedMatchTimesDisabled) {
            const allottedTime = getDefaultAllottedTimeForScoringType(scoringTypeId, eventAllottedMatchTimes, scoringTypes);
            formApi.setValue('allottedMatchTime', allottedTime);
        }
    };

    const updateWhichPlayOffDrawsToDisplay = (formState) => {
        const { values } = formState;
        if (!values) {
            return;
        }

        if (isInEditMode && drawDataFromEditSpecificApi.categoryTypeId === drawCategoryTypeIds.GROUPED) {
            const playOffDrawsToDisplay = playOffDrawDetailsForAutomatedDraws.filter((playOffDrawDetail) => {
                const isDrawEliminationTypeValid = playOffDrawDetail.drawEliminationTypeId === parseInt(values?.eliminationTypeId);

                if (isDrawEliminationTypeValid && playOffDrawDetail.needsExtraFieldsToCheck) {
                    const fieldMatchingCriteria = playOffDrawDetail.extraFields.filter((extraField) => {
                        const valueToCheck = values[extraField.name];

                        return valueToCheck && parseInt(values[extraField.name]) !== extraField.value;
                    });

                    return fieldMatchingCriteria.length > 0;
                }

                return isDrawEliminationTypeValid;
            });

            setPlayOffDrawOptions(playOffDrawsToDisplay);
        } else {
            const eliminationTypeId = getDrawEliminationTypeId(values);
            const playOffDrawsToDisplay = playOffDrawDetails.filter((playOffDrawDetail) => {
                const isDrawEliminationTypeValid = playOffDrawDetail.drawEliminationTypeId === parseInt(eliminationTypeId);

                if (isDrawEliminationTypeValid && playOffDrawDetail.needsExtraFieldsToCheck) {
                    const fieldMatchingCriteria = playOffDrawDetail.extraFields.filter((extraField) => {
                        const valueToCheck = values[extraField.name];

                        return valueToCheck && parseInt(values[extraField.name]) !== extraField.value;
                    });

                    return fieldMatchingCriteria.length > 0;
                }

                return isDrawEliminationTypeValid;
            });

            setPlayOffDrawOptions(playOffDrawsToDisplay);
        }
    };

    const updateWhichScoringTypeOverridesToDisplay = (formState) => {
        const { values } = formState;
        if (!values) {
            return;
        }

        if (isInEditMode && drawDataFromEditSpecificApi.categoryTypeId === drawCategoryTypeIds.GROUPED) {
            const scoringFormatOverrides = scoringTypeOverridesForAutomatedDraws.filter((scoringFormatOverride) => {
                const isDrawEliminationTypeValid = scoringFormatOverride.drawEliminationTypeId === parseInt(values?.eliminationTypeId);

                if (isDrawEliminationTypeValid && scoringFormatOverride.isForPlayOffDraw) {
                    return values[scoringFormatOverride.playOffDrawFieldName];
                }

                return isDrawEliminationTypeValid;
            });

            setScoringFormatOverrideOptions(scoringFormatOverrides);
        } else {
            const eliminationTypeId = getDrawEliminationTypeId(values);
            const scoringFormatOverrides = scoringTypeOverrides.filter((scoringFormatOverride) => {
                const isDrawEliminationTypeValid = scoringFormatOverride.drawEliminationTypeId === parseInt(eliminationTypeId);

                if (isDrawEliminationTypeValid && scoringFormatOverride.isForPlayOffDraw) {
                    return values[scoringFormatOverride.playOffDrawFieldName];
                }

                if (isDrawEliminationTypeValid &&
                    scoringFormatOverride.isFinalRound &&
                    scoringFormatOverride.categoryTypeId === drawCategoryTypeIds.AUTO_PLAYOFF) {
                    return values.drawSize > 2 && values.categoryTypeId === drawCategoryTypeIds.AUTO_PLAYOFF;
                }

                if (isDrawEliminationTypeValid &&
                    scoringFormatOverride.isFinalRound) {
                    return values.drawSize > 2 && values.categoryTypeId !== drawCategoryTypeIds.AUTO_PLAYOFF;
                }

                return isDrawEliminationTypeValid;
            });

            setScoringFormatOverrideOptions(scoringFormatOverrides);
        }
    };

    const updateDrawSize = (eliminationTypeId, formatTypeId) => {
        const label = (formatTypeId === divisionFormatTypeIds.DOUBLES || formatTypeId === divisionFormatTypeIds.DINGLES) ? 'teams' : 'players';
        let sizesUpdate;
        if (eliminationTypeId === drawEliminationTypeIds.COMPASS) {
            sizesUpdate = drawSizesCompass(label);
        } else if (eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY) {
            sizesUpdate = drawSizesMP(label);
        } else if (eliminationTypeId === drawEliminationTypeIds.FEED_IN_CONSOLATION) {
            sizesUpdate = ficOrFibDrawSizes(label);
        } else if (eliminationTypeId === drawEliminationTypeIds.FEED_IN_BACK_DRAW) {
            sizesUpdate = ficOrFibDrawSizes(label);
        } else {
            sizesUpdate = drawSizes(label);
        }
        setSelectableDrawSizes(sizesUpdate);

        return sizesUpdate;
    };

    const onFormChange = (formState) => {
        const { formatTypeId } = formState.values;
        const eliminationTypeId = getDrawEliminationTypeId(formState.values);
        updateDrawSize(parseInt(eliminationTypeId), parseInt(formatTypeId));

        updateWhichPlayOffDrawsToDisplay(formState);
        updateWhichScoringTypeOverridesToDisplay(formState);
    };

    const onFormatTypeIdChange = (formApi) => (formatTypeId) => {
        const { values } = formApi;

        const eliminationTypeId = getDrawEliminationTypeId(values);

        // If value in drawSize is not available in updated draw sizes clear the field
        const updatedSizes = updateDrawSize(parseInt(eliminationTypeId), parseInt(formatTypeId));
        if (values.drawSize) {
            const currentDrawSize = updatedSizes.find((size) => size.id === values.drawSize);
            if (!currentDrawSize) {
                formApi.setValue('drawSize', '');
                formApi.setValue('divisions', '');
            }
        }
    };

    const onGenderIdChange = (formApi) => () => {
        formApi.setValue('divisions', '');
    };

    const onDrawEliminationChange = (formApi, isFromParentDrawEliminationChange = false) => (eliminationTypeId) => {
        const { values } = formApi;

        setAllowRoundResize(parseInt(eliminationTypeId) === drawEliminationTypeIds.AD_HOC);

        // If value in drawSize is not available in updated draw sizes clear the field
        const updatedSizes = updateDrawSize(parseInt(eliminationTypeId), parseInt(values.formatTypeId));
        if (values.drawSize) {
            const currentDrawSize = updatedSizes.find((size) => size.id === values.drawSize);
            if (!currentDrawSize) {
                formApi.setValue('drawSize', '');
            }
        }

        setShowFicFormatTypeField(parseInt(eliminationTypeId) === drawEliminationTypeIds.FEED_IN_CONSOLATION);
        // If not FIC clear FicFormatType value if already set
        if (parseInt(eliminationTypeId) !== drawEliminationTypeIds.FEED_IN_CONSOLATION) {
            formApi.reset('ficFormatTypeId');
        }

        setIsDoubleEliminationDraw(parseInt(eliminationTypeId) === drawEliminationTypeIds.DOUBLE_ELIMINATION);
        // If not Double Elim clear notAllowMovingWinnersToMainFromBackDraw value if already set
        if (parseInt(eliminationTypeId) !== drawEliminationTypeIds.DOUBLE_ELIMINATION) {
            formApi.reset('notAllowMovingWinnersToMainFromBackDraw');
        }

        // If not Feed in back draw clear feedOutRound value if already set
        if (parseInt(eliminationTypeId) !== drawEliminationTypeIds.FEED_IN_BACK_DRAW) {
            formApi.reset('feedOutRound');
        }

        // If not from prodraw parent draw elimination change and not pro draw clear playoff qualifying, pre qualifying,
        // parentDrawEliminationId and other fields
        if (!isFromParentDrawEliminationChange && parseInt(eliminationTypeId) !== drawEliminationTypeIds.PRO_DRAW) {
            formApi.reset('qualifyingDraw');
            formApi.reset('preQualifyingDraw');
            formApi.reset('parentDrawEliminationTypeId');
        }

        if (parseInt(eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS) {
            formApi.setValue('isQualifier', false);
        }

        // Clear playoff field values if already set
        clearAllPlayOffDrawValues(formApi);

        // Clear playoff field values if already set
        clearAllScoringFormatOverrideValues(formApi);
    };

    const onDrawSizeChange = (formApi) => (drawSizeId) => {
        const { values } = formApi;
        const eliminationTypeId = getDrawEliminationTypeId(values);
        setIsDrawSizeChanged(true);
        clearAllPlayOffDrawValues(formApi);
        clearAllScoringFormatOverrideValues(formApi);
        if (parseInt(eliminationTypeId) === drawEliminationTypeIds.FEED_IN_CONSOLATION) {
            formApi.setValue('ficFormatTypeId', '');
            if (parseInt(drawSizeId) === 16) {
                formApi.setValue('ficFormatTypeId', ficFormats.FIC_QF);
                setFicFormatTypes(ficFormatTypeIdsDisabledFIC16);
            } else {
                setFicFormatTypes(ficFormatTypeIds);
            }
        }
    };

    const handleDateChange = (fieldName, formApi) => (dateTime) => {
        setDateToDisplay(dateTime);
        formApi.setValue(fieldName, dateTime?.toISOString() || null);
    };

    const onChangeFibFeedOutRound = (formApi) => () => {
        clearAllPlayOffDrawValues(formApi);
        clearAllScoringFormatOverrideValues(formApi);
    };

    const onChangeFicFormatTypeId = (formApi) => () => {
        clearAllPlayOffDrawValues(formApi);
        clearAllScoringFormatOverrideValues(formApi);
    };

    const clearAllPlayOffDrawValues = (formApi) => {
        playOffDrawDetails.forEach((playOffDrawDetail) => {
            formApi.setValue(playOffDrawDetail.fieldName, false);
        }, []);
    };

    const clearAllScoringFormatOverrideValues = (formApi) => {
        scoringFormatOverrideOptions.forEach((scoringTypeOverride) => {
            const overrideValue = formApi.getValue(scoringTypeOverride.id);

            if (overrideValue) {
                formApi.reset(scoringTypeOverride.id);
            }
        }, []);
    };

    const onPlayOffDrawCheckChange = (formApi) => (value, playOffDrawOption) => {
        const playOffScoringOverride = scoringTypeOverrides.find((sfoOption) => {
            return sfoOption.isForPlayOffDraw &&
                (sfoOption.playOffDrawFieldName === playOffDrawOption.fieldName);
        });

        // If playoff draw is removed clear its respective scoring type override
        if (playOffScoringOverride && !value) {
            formApi.reset(playOffScoringOverride.id);
        }
    };

    const getQualifierDrawDefaultValuesFromData = (qualifierDrawData) => {
        if (qualifierDrawData) {
            return {
                enabled: true,
                ...qualifierDrawData,
            };
        }

        return {
            enabled: false,
        };
    };

    const getDefaultValuesFromDrawSpecificData = (data, parentDraw, drawDivision) => {
        const {
            officialsTypeId,
            ballTypeId,
            scoringTypeAndAllottedMatchTimeOverrides,
            proDrawQualifierDraw,
            proDrawPreQualifierDraw,
            associatedDivisionIds,
            ...others
        } = data;

        const hasGroupDrawParent = parentDraw?.categoryTypeId === drawCategoryTypeIds.GROUPED;
        const eliminationTypeId = getEliminationTypeIdFromDrawData(data);
        const parentDrawEliminationTypeId = getParentDrawEliminationTypeIdFromDrawData(data);

        const updatedOverrides = parseInt(eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS
            ? convertScoringOverrideProCategoryToMain(scoringTypeAndAllottedMatchTimeOverrides)
            : scoringTypeAndAllottedMatchTimeOverrides;

        const scoringTypeOverrideDefaultValues = getFormValuesFromEventDrawScoringTypeOverrides(
            updatedOverrides,
            data.eliminationTypeId,
        );

        let qualifyingDraw = null;
        let preQualifyingDraw = null;

        if (eliminationTypeId === drawEliminationTypeIds.PRO_DRAWS) {
            qualifyingDraw = getQualifierDrawDefaultValuesFromData(proDrawQualifierDraw);
            preQualifyingDraw = getQualifierDrawDefaultValuesFromData(proDrawPreQualifierDraw);
        }

        let divisionIds;
        if (drawDivision) {
            divisionIds = {};
            if (hasGroupDrawParent) {
                // eslint-disable-next-line no-unused-expressions
                drawDivision[parentDraw.id]?.forEach(divId => {
                    divisionIds[`_${divId}`] = true;
                });
            } else {
                // eslint-disable-next-line no-unused-expressions
                drawDivision[draw.id]?.forEach(divId => {
                    divisionIds[`_${divId}`] = true;
                });
            }
        }

        let playersInPlayOffDraw;
        if (data.categoryTypeId === drawCategoryTypeIds.GROUPED && data.createPlayoffDraw) {
            const groupPlayersAdvancingToPlayoffCount = parseInt(data.groupPlayersAdvancingToPlayoffCount);
            playersInPlayOffDraw = parseInt(groupPlayersAdvancingToPlayoffCount) * parseInt(data.groupsCount);
        }

        const updatedBallTypeId = parseInt(ballTypeId) >= 0 ? ballTypeId : ballTypeIds.YELLOW;

        return {
            ...others,
            areOfficialsAllowed: officialsTypeId === officialsTypeIds.YES,
            ballTypeId: updatedBallTypeId.toString(),
            ...scoringTypeOverrideDefaultValues,
            eliminationTypeId,
            parentDrawEliminationTypeId,
            qualifyingDraw,
            preQualifyingDraw,
            divisionIds,
            playersInPlayOffDraw,
            ...(hasGroupDrawParent ? { groupName: parentDraw?.name } : {}),
        };
    };

    const onChangeAutoFillType = (formApi) => (autofillTypeId) => {
        if (parseInt(autofillTypeId) === drawAutofillTypeIds.LEVEL_BASED) {
            formApi.reset('groupsCount');
        } else {
            formApi.reset('matchesGuaranteed');
        }

        // Clear playoff field values if already set
        clearAllPlayOffDrawValues(formApi);
        clearAllScoringFormatOverrideValues(formApi);
    };

    const onGroupPlayersAdvancingChangeForGroupDraws = (formApi) => (groupPlayersAdvancingToPlayoffCount = 1) => {
        const groupsCount = formApi.getValue('groupsCount');
        const playersCount = parseInt(groupPlayersAdvancingToPlayoffCount) * parseInt(groupsCount);
        formApi.setValue('playersInPlayOffDraw', playersCount);

        if (isInEditMode && playersCount >= 4) {
            formApi.setValue('createPlayoffBronzeMatch', drawDataFromEditSpecificApi?.createPlayoffBronzeMatch || false);
        } else if (playersCount < 4) {
            formApi.setValue('createPlayoffBronzeMatch', false);
        }
    };

    const onGroupPlayersAdvancingChangeForNonGroupDraws = (formApi) => (groupPlayersAdvancingToPlayoffCount = 2) => {
        const playersCount = parseInt(groupPlayersAdvancingToPlayoffCount);

        if (isInEditMode && playersCount >= 4) {
            formApi.setValue('createPlayoffBronzeMatch', drawDataFromEditSpecificApi?.createPlayoffBronzeMatch || false);
        } else if (playersCount < 4) {
            formApi.setValue('createPlayoffBronzeMatch', false);
        }
    };

    const resetGroupPlayersAdvancingToPlayoffCount = (formApi) => () => {
        if (isInEditMode && drawDataFromEditSpecificApi?.groupPlayersAdvancingToPlayoffCount) {
            formApi.setValue('groupPlayersAdvancingToPlayoffCount', drawDataFromEditSpecificApi?.groupPlayersAdvancingToPlayoffCount);
        }
    };

    const onGroupsCountChange = (formApi) => (groupsCount) => {
        const createPlayoffDraw = formApi.getValue('createPlayoffDraw');
        if (createPlayoffDraw) {
            const groupPlayersAdvancingToPlayoffCount = formApi.getValue('groupPlayersAdvancingToPlayoffCount');
            const playersCount = parseInt(groupPlayersAdvancingToPlayoffCount) * parseInt(groupsCount);
            formApi.setValue('playersInPlayOffDraw', playersCount);
            if (playersCount < 4) {
                formApi.reset('createPlayoffBronzeMatch');
            }
        } else {
            formApi.reset('playersInPlayOffDraw');
            formApi.reset('createPlayoffBronzeMatch');
        }
    };

    useEffect(() => {
        const defaultValue = moment.tz.guess();
        if (allowMomentTimeZoneOverride && timeZone) {
            moment.tz.setDefault(timeZone.value);
        }

        return () => {
            if (allowMomentTimeZoneOverride) {
                moment.tz.setDefault(defaultValue);
            }
        };
    }, [allowMomentTimeZoneOverride, timeZone]);

    useEffect(() => {
        const getDrawData = async () => {
            const { data } = await getDrawDataForEdit(getApiAxiosInstance(), eventId, draw.id);
            setDrawDataFromEditSpecificApi(data);
        };
        if (isInEditMode && !drawDataFromEditSpecificApi) {
            getDrawData();
        }
    }, [isInEditMode, sportTypeId, draw, eventId, drawDataFromEditSpecificApi]);

    useEffect(() => {
        if (drawDataFromEditSpecificApi) {
            if (drawDataFromEditSpecificApi.startDateTimeUtc) {
                const dateTime = moment.utc(drawDataFromEditSpecificApi.startDateTimeUtc).tz(timeZone?.value);
                setDateToDisplay(dateTime);
            }

            const isFicDrawType = parseInt(drawDataFromEditSpecificApi.eliminationTypeId) === drawEliminationTypeIds.FEED_IN_CONSOLATION;
            setShowFicFormatTypeField(isFicDrawType);

            const isDoubleEliminationDrawType = parseInt(drawDataFromEditSpecificApi.eliminationTypeId) === drawEliminationTypeIds.DOUBLE_ELIMINATION;
            setIsDoubleEliminationDraw(isDoubleEliminationDrawType);

            const isAdHocDrawType = parseInt(drawDataFromEditSpecificApi.eliminationTypeId) === drawEliminationTypeIds.AD_HOC;
            setAllowRoundResize(isAdHocDrawType);

            updateDrawSize(parseInt(drawDataFromEditSpecificApi.eliminationTypeId), parseInt(drawDataFromEditSpecificApi.formatTypeId));
        }
    }, [drawDataFromEditSpecificApi, timeZone]);

    useEffect(() => {
        let formState = {};
        if (isInEditMode) {
            if (drawDataFromEditSpecificApi) {
                formState = {
                    values: {
                        eliminationTypeId: getEliminationTypeIdFromDrawData(drawDataFromEditSpecificApi),
                        parentDrawEliminationTypeId: getParentDrawEliminationTypeIdFromDrawData(drawDataFromEditSpecificApi),
                        ficFormatTypeId: parseInt(drawDataFromEditSpecificApi.ficFormatTypeId),
                        isThreeFourPlayoffDraw: drawDataFromEditSpecificApi.isThreeFourPlayoffDraw,
                        isFiveSixPlayoffDraw: drawDataFromEditSpecificApi.isFiveSixPlayoffDraw,
                        createPlayoffDraw: drawDataFromEditSpecificApi.createPlayoffDraw,
                        createPlayoffBronzeMatch: drawDataFromEditSpecificApi.createPlayoffBronzeMatch,
                        drawSize: drawDataFromEditSpecificApi.drawSize,
                        roundSize: drawDataFromEditSpecificApi.roundSize,
                        notAllowMovingWinnersToMainFromBackDraw: drawDataFromEditSpecificApi.notAllowMovingWinnersToMainFromBackDraw,
                        awardMedal: drawDataFromEditSpecificApi.awardMedal,
                        categoryTypeId: drawDataFromEditSpecificApi.categoryTypeId,
                    },
                };
            }
        } else {
            formState = {
                values: {
                    eliminationTypeId: defaultEliminationTypeId,
                },
            };
        }

        if (playOffDrawOptions.length === 0) {
            updateWhichPlayOffDrawsToDisplay(formState);
        }

        if (scoringFormatOverrideOptions.length === 0) {
            updateWhichScoringTypeOverridesToDisplay(formState);
        }
    }, [isInEditMode, drawDataFromEditSpecificApi]);

    return {
        isAllottedTimeFlagEnabled,
        selectableDrawEliminationTypes,
        proDrawEliminationTypes,
        selectableScoringTypes,
        drawDataFromEditSpecificApi,
        playOffDrawOptions,
        scoringFormatOverrideOptions,
        dateToDisplay,
        showFicFormatTypeField,
        allowRoundResize,
        selectableDrawSizes,
        ficFormatTypes,
        isDrawSizeChanged,
        defaultEliminationTypeId,
        isDoubleEliminationDraw,
        onChangeScoringType,
        onFormChange,
        onFormatTypeIdChange,
        onGenderIdChange,
        onDrawEliminationChange,
        onDrawSizeChange,
        handleDateChange,
        onChangeFicFormatTypeId,
        onChangeFibFeedOutRound,
        getScoringFormats,
        onPlayOffDrawCheckChange,
        getDefaultValuesFromDrawSpecificData,
        onChangeAutoFillType,
        onGroupPlayersAdvancingChangeForGroupDraws,
        onGroupPlayersAdvancingChangeForNonGroupDraws,
        resetGroupPlayersAdvancingToPlayoffCount,
        onGroupsCountChange,
    };
};

export default useDrawEdit;
